import React, { useState } from 'react';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'components/ThemedECharts';

import Selector from 'components/DesignSystem/Selector';
import GraficoTelaCheia from 'components/Relatorios/shared/Mobile/GraficoTelaCheia';
import SubtitleSemiBold from 'components/Typography/SubtitleSemiBold';
import { formatNumberBR } from 'components/Relatorios/shared/RelatorioTable';
import { DARK_BLUE } from 'constants/colors';
import { comFormatadores, criarValueFormatter } from 'helper/Relatorios/Dessem/chartOption';
import { DessemBalancoLinha } from 'types/Relatorios/Dessem';

import './BalancoChart.scss';

interface Props {
  colunas: string[];
  linhas: DessemBalancoLinha[];
  /** Título do bloco (`balanco.titulo` do payload); sem ele, "Balanço Energético". */
  titulo?: string | null;
}

/**
 * Tooltip e eixo Y em pt-BR (até 3 casas), com o `option` do BE e no gráfico montado aqui. Sem o
 * `formatter`, o ECharts escreve o eixo no formato inglês (`50,000`).
 */
const FORMATADORES = { tooltip: criarValueFormatter(formatNumberBR), eixoY: formatNumberBR };

/**
 * Rótulo de cada barra no gráfico montado aqui, em pt-BR. No escopo do módulo: com a mesma
 * referência entre renders, o `echarts-for-react` vê o `option` igual e não chama o `setOption`,
 * que com `notMerge` recriaria o gráfico a cada mudança do store.
 */
const rotuloDaBarra = (params: any): string => formatNumberBR(params.value);

export default function BalancoChart({ colunas, linhas, titulo }: Props) {
  const [subsistema, setSubsistema] = useState(linhas[0]?.subsistema);
  const linha = linhas.find((l) => l.subsistema === subsistema) ?? linhas[0];

  const optionLocal = (): EChartsOption => ({
    xAxis: { type: 'category', data: colunas },
    yAxis: { type: 'value' },
    series: [{
      type: 'bar',
      data: linha.valores,
      color: DARK_BLUE, // tema: cor de dado
      label: {
        show: true,
        position: 'top',
        formatter: rotuloDaBarra,
      },
    }],
    tooltip: { trigger: 'axis' },
    grid: {
      left: '5%', right: '5%', top: '16%', bottom: '10%', containLabel: true,
    },
  });

  // Um `option` do BE por subsistema (contrato §2.14), como veio; sem ele, montado aqui.
  const option = comFormatadores(linha.option ?? optionLocal(), FORMATADORES);

  return (
    <>
      <div className="dessem-balanco-chart__header">
        <SubtitleSemiBold>{titulo ?? 'Balanço Energético'}</SubtitleSemiBold>
        <Selector
          id="dessem-balanco-subsistema"
          label="Subsistema"
          containerClassName="dessem-balanco-chart__selector"
          value={subsistema}
          setValue={setSubsistema}
        >
          {linhas.map((l) => (
            <option key={l.subsistema} value={l.subsistema}>{l.subsistema}</option>
          ))}
        </Selector>
      </div>
      <GraficoTelaCheia>
        {(telaCheia) => (
          <ReactECharts
            option={option}
            // Trocar de subsistema, ou entre o `option` do BE e o montado aqui, não deixa chave
            // velha no gráfico.
            notMerge
            style={telaCheia ? { height: '100%', width: '100%' } : { height: 220 }}
          />
        )}
      </GraficoTelaCheia>
    </>
  );
}
